import { storeAuthType } from '../useAuthType';
import { GENERIC_ERROR_MESSAGE } from '../useLogin/constants';
import { FormEvent, useEffect, useState } from 'react';
import { addTruthyParamsToUrl } from '@zapier/url-utils';

import {
  defaultRouteTo,
  discoverAccount,
  logout,
  nextForSsoRedirect,
} from '../../utils';
import { RouteTo } from '../../types';
import {
  LoginWithIdentityProviderErrors,
  LoginWithIdentityProviderOutput,
  LoginWithIdentityProviderStatus,
  useLoginWithIdentityProviderInput,
} from './types';

async function redirectToSSO(
  email: string,
  next: string | undefined,
  routeTo: RouteTo
): Promise<LoginWithIdentityProviderOutput> {
  // First ensure we are logged out
  await logout();

  // Try to discovery the user's account
  const discoveredAccount = await discoverAccount({
    attemptStoredAccount: true,
    email: email,
  });

  // We couldn't find the user's account
  if (!discoveredAccount) {
    return {
      didRedirect: false,
      errors: {
        email: "That Zapier account doesn't exist.",
      },
    };
  }

  // We did discover the account but it doesn't use SAML.
  if (!discoveredAccount.hasSamlSso || !discoveredAccount.ssoUrl) {
    return {
      didRedirect: false,
      errors: {
        email: "That Zapier account isn't using an Identity Provider.",
      },
    };
  }

  // We need to redirect to start SAML
  routeTo(
    addTruthyParamsToUrl(discoveredAccount.ssoUrl, {
      email: email,
      next: nextForSsoRedirect(next),
    })
  );

  return {
    didRedirect: true,
  };
}

/**
 * React hook that controls the data / flow of logging a user in with a SAML provider
 */
export function useLoginWithSAMLSSO(
  input: useLoginWithIdentityProviderInput = {}
) {
  const [email, setEmail] = useState('');
  const [hasTouched, setHasTouched] = useState(false);
  const [status, setStatus] =
    useState<LoginWithIdentityProviderStatus>('initial');
  const [formErrors, setErrors] = useState<LoginWithIdentityProviderErrors>({});

  const routeTo = input.routeTo || defaultRouteTo;

  // Only allow users to submit when the email field has something in it
  const canSubmit = email.length > 0;

  // force the input email if the input changes
  useEffect(() => {
    if (input.email) {
      setEmail(input.email);
    }
  }, [input.email]);

  // Update the e-mail value if the user has not entered anything yet, and the default email passed in props changes
  useEffect(() => {
    if (input.defaultEmail && !hasTouched) {
      setEmail(input.defaultEmail);
    }
  }, [hasTouched, input.defaultEmail]);

  /**
   * Handles changes in form data
   */
  function handleChange(newEmail: string) {
    setEmail(newEmail);
    setHasTouched(true);
  }

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    // Show that we are submitting
    setStatus('submitting');

    try {
      const redirectToSSOResult = await redirectToSSO(
        email,
        input.next,
        routeTo
      );

      // All went well, we've redirected the user.
      if (redirectToSSOResult.didRedirect) {
        storeAuthType('saml');
        return;
      }

      // We ran into a problem. This likely means that the user entered a wrong email address or that they don't have SSO.
      if (Object.keys(redirectToSSOResult?.errors || {}).length > 0) {
        setErrors(redirectToSSOResult?.errors || {});
        setStatus('error');
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      setErrors({ error: GENERIC_ERROR_MESSAGE });
      setStatus('error');
    }
  }

  return {
    // Is the form allowed to be submitted
    canSubmit,
    // Current field data in the form
    email,
    // Current errors in the form including field level errors and more generic form-level errors
    formErrors,
    // Callback to handle changes to form data
    handleChange,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Previous login detected from auth_type cookie
    status,
  };
}
